import { observer } from "mobx-react-lite"

import type { VenammSellStore } from "@/domains/venamm"
import { Button } from "@/components/button"
import { Lozenge } from "@/domains/common/components/lozenge"
import { TickIcon } from "@/domains/icons"

export const VenammStateButtonsMap = observer(
  ({
    store,
    onCancel,
    onStartSell,
    onReset,
    onRetryReset,
    onRetryQuote,
    onApprove,
    onRetryApprove,
    onSell,
    onRetrySell,
  }: {
    store: VenammSellStore
    onCancel: () => void
    onStartSell: () => void
    onReset: () => void
    onRetryReset: () => void
    onRetryQuote: () => void
    onApprove: () => void
    onRetryApprove: () => void
    onSell: () => void
    onRetrySell: () => void
  }) => {
    if (store.state === "idle") {
      return (
        <div className="flex items-center justify-end gap-2">
          <SellButton onClick={onStartSell} disabled={!store.lock} />
        </div>
      )
    }

    if (store.state === "quote-ready") {
      return (
        <div className="flex items-center justify-end gap-2">
          <SellButton onClick={onStartSell} disabled={!store.quote} />
        </div>
      )
    }

    if (["quoting", "checking-reset-required", "checking-approval"].includes(store.state)) {
      return (
        <div className="flex items-center justify-end gap-2">
          <SellButton onClick={() => {}} disabled={true} />
        </div>
      )
    }

    switch (store.state) {
      case "quote-failed":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelButton onCancel={onCancel} />
            <RetryButton onRetry={onRetryQuote} />
          </div>
        )

      case "reset-required":
        return (
          <div className="flex items-center justify-end">
            <ResetButton onClick={onReset} />
          </div>
        )

      case "resetting":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )

      case "reset-failed":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelButton onCancel={onCancel} />
            <RetryButton onRetry={onRetryReset} />
          </div>
        )

      case "approving":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )

      case "waiting-for-approval-confirmation":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )

      case "approve-failed":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelButton onCancel={onCancel} />
            <RetryButton onRetry={onRetryApprove} />
          </div>
        )

      case "selling":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )

      case "sell-failed":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelButton onCancel={onCancel} />
            <RetryButton onRetry={onRetrySell} />
          </div>
        )

      case "complete":
        return (
          <div className="flex items-center justify-end gap-2">
            <CompletedButton />
          </div>
        )

      default:
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )
    }
  },
)

const CancelSoftButton = observer(({ onCancel, className }: { onCancel: () => void; className?: string }) => {
  return (
    <Button onClick={onCancel} variant="ghost" size="sm" className={className}>
      Cancel
    </Button>
  )
})

const CancelButton = observer(({ onCancel, className }: { onCancel: () => void; className?: string }) => {
  return (
    <Button onClick={onCancel} variant="ghost" size="sm" className={className}>
      Cancel
    </Button>
  )
})

const RetryButton = observer(({ onRetry, className }: { onRetry: () => void; className?: string }) => {
  return (
    <Button variant="primary" onClick={onRetry} size="sm" className={className}>
      Retry
    </Button>
  )
})

const ResetButton = observer(({ onClick, className }: { onClick: () => void; className?: string }) => {
  return (
    <Button variant="primary" onClick={onClick} size="sm" className={className}>
      Reset
    </Button>
  )
})

const SellButton = observer(
  ({ onClick, className, disabled }: { onClick: () => void; className?: string; disabled?: boolean }) => {
    return (
      <Button variant="primary" onClick={onClick} size="sm" className={className} disabled={disabled}>
        Sell
      </Button>
    )
  },
)

const CompletedButton = observer(() => {
  return (
    <Lozenge color="success" size="md" contentLeft={<TickIcon size={16} />}>
      Sold
    </Lozenge>
  )
})

